import { Link } from "react-router-dom";
import { useLang, useT } from "../i18n";
import { ORGANIZATION_ID, breadcrumbLd, useSeo, webPageLd } from "../lib/seo";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

/** /contact: support e-mail, phone and postal address. */
export default function Contact() {
  const t = useT();
  const page = t.contactPage;
  const lang = useLang();
  useSeo({
    title: t.seo.contact.title,
    description: t.seo.contact.description,
    path: "/contact",
    jsonLd: [
      {
        ...webPageLd("ContactPage", t.seo.contact.title, t.seo.contact.description, "/contact", lang),
        mainEntity: {
          "@id": ORGANIZATION_ID,
          "@type": "Organization",
          name: "Le Coin VTC",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: page.email,
            ...(page.phone.trim() && { telephone: page.phone.trim() }),
            availableLanguage: ["French", "English"],
            hoursAvailable: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00" },
          },
        },
      },
      breadcrumbLd([[t.seo.breadcrumbHome, "/"], [t.seo.contact.title]]),
    ],
  });

  // No phone number yet: the card shows the support hours only (no call link)
  const phone = page.phone.trim();
  const cards: { title: string; body: string; href?: string; note?: string }[] = [
    { title: page.emailTitle, body: page.email, href: `mailto:${page.email}`, note: page.emailNote },
    phone
      ? { title: page.phoneTitle, body: phone, href: `tel:${phone.replace(/\s/g, "")}`, note: `${page.hoursTitle}: ${page.hours}\n${page.holidays}` }
      : { title: page.hoursTitle, body: page.hours, note: page.holidays },
    { title: page.addressTitle, body: page.address },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />

      <main className="mx-auto w-full max-w-300 flex-1 px-4 pt-12 pb-24 sm:px-6">
        <h1 className="m-0 text-2xl sm:text-3xl font-medium tracking-[-0.01em] sm:text-[40px]">{page.title}</h1>
        <p className="mt-2 mb-1 max-w-150 text-[15px] leading-normal text-muted">{page.intro}</p>
        <p className="mt-0 mb-8 max-w-150 text-[15px] leading-normal text-muted">{page.response}</p>

        <div className="grid gap-3 sm:grid-cols-3">
          {cards.map((card) => (
            <div key={card.title} className="rounded-card border border-line-strong p-5">
              <div className="text-xs text-muted">{card.title}</div>
              {card.href ? (
                <a href={card.href} className="mt-1.5 block whitespace-pre-line text-sm font-medium text-ink no-underline hover:text-brand">
                  {card.body}
                </a>
              ) : (
                <p className="m-0 mt-1.5 whitespace-pre-line text-sm font-medium leading-normal">{card.body}</p>
              )}
              {card.note && <p className="m-0 mt-2 whitespace-pre-line text-xs leading-normal text-muted">{card.note}</p>}
            </div>
          ))}
        </div>

        <a
          href={`mailto:${page.email}`}
          className="mt-6 inline-flex h-10 items-center rounded bg-brand px-4.5 text-[13px] font-medium text-white no-underline hover:bg-brand-dark"
        >
          {page.emailCta}
        </a>

        <p className="mt-8 mb-0 text-[13px] text-muted">
          {page.faqText}{" "}
          <Link to="/how-it-works" className="text-brand-link">
            {page.faqLink}
          </Link>
          .
        </p>
      </main>

      <Footer />
    </div>
  );
}
